"use client"

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import type { PlanId } from '@/lib/branchTypes'
import type { GraphDatum } from './types'
import {
  useLossOfControlEditor,
  type LossOfControlBound,
} from './LossOfControlEditorProvider'

type EditorDotsProps = {
  activePlan: PlanId
  effectiveCappedTimestamp: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
}

type DragState = {
  pointerId: number
  plan: PlanId
  bound: LossOfControlBound
  dateString: string
}

const MIN_VALUE = 0

const dateStringToTimestamp = (dateString: string) =>
  new Date(`${dateString}T00:00:00Z`).getTime()

// Linear inverse of `yForValue`. Treats yForValue as an affine function:
//   yForValue(v) = yForValue(0) + v * slope
// Caller must ensure the y-axis is linear (the playbook graph uses
// `useLogScale={false}`); for log scales this would silently produce wrong
// values.
const createValueForY = (yForValue: (value: number) => number) => {
  const yAtZero = yForValue(0)
  const yAtOne = yForValue(1)
  const slope = yAtOne - yAtZero
  return (screenY: number): number => {
    if (slope === 0) return 0
    return (screenY - yAtZero) / slope
  }
}

const roundForStorage = (value: number): number => {
  const clamped = Math.max(MIN_VALUE, value)
  // Two decimals matches the precision already used in
  // extrapolatedGraphData.json (e.g. 2.06, 21.8, 1230.5).
  return Math.round(clamped * 100) / 100
}

const BOUNDS_PROPS_BY_BOUND: Record<LossOfControlBound, {
  fill: string
  stroke: string
  label: string
}> = {
  min: {
    fill: '#1d4ed8',
    stroke: '#fffff8',
    label: 'min',
  },
  max: {
    fill: '#b91c1c',
    stroke: '#fffff8',
    label: 'max',
  },
}

const DOT_RADIUS = 5
const DOT_HIT_RADIUS = 9

const EditorDot = ({
  cx,
  cy,
  bound,
  isDragging,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: {
  cx: number
  cy: number
  bound: LossOfControlBound
  isDragging: boolean
  onPointerDown: (event: ReactPointerEvent<SVGCircleElement>) => void
  onPointerMove: (event: ReactPointerEvent<SVGCircleElement>) => void
  onPointerUp: (event: ReactPointerEvent<SVGCircleElement>) => void
  onPointerCancel: (event: ReactPointerEvent<SVGCircleElement>) => void
}) => {
  const { fill, stroke } = BOUNDS_PROPS_BY_BOUND[bound]
  return (
    <g pointerEvents="auto">
      <circle
        cx={cx}
        cy={cy}
        r={DOT_HIT_RADIUS}
        fill="transparent"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{ cursor: 'ns-resize', touchAction: 'none' }}
      />
      <circle
        cx={cx}
        cy={cy}
        r={DOT_RADIUS}
        fill={fill}
        stroke={stroke}
        strokeWidth={1.5}
        pointerEvents="none"
        style={{
          opacity: isDragging ? 1 : 0.85,
          filter: isDragging
            ? 'drop-shadow(0 0 2px rgba(0,0,0,0.4))'
            : undefined,
        }}
      />
    </g>
  )
}

export const LossOfControlEditorDots = ({
  activePlan,
  effectiveCappedTimestamp,
  xForTimestamp,
  yForValue,
}: EditorDotsProps) => {
  const { lossOfControlByPlan, setLossOfControlValue } = useLossOfControlEditor()
  const [dragState, setDragState] = useState<DragState | null>(null)

  const valueForY = useMemo(() => createValueForY(yForValue), [yForValue])

  const planBounds = lossOfControlByPlan[activePlan]

  // We only show editor dots for years that are currently visible in the
  // chart (timestamp <= effectiveCappedTimestamp). Editing values for years
  // that are still hidden by the playbook timeline would be confusing because
  // the dragged dot wouldn't be visible.
  const visibleMinData = useMemo(
    () => planBounds.min.filter(datum =>
      dateStringToTimestamp(datum.date) <= effectiveCappedTimestamp
    ),
    [planBounds.min, effectiveCappedTimestamp],
  )
  const visibleMaxData = useMemo(
    () => planBounds.max.filter(datum =>
      dateStringToTimestamp(datum.date) <= effectiveCappedTimestamp
    ),
    [planBounds.max, effectiveCappedTimestamp],
  )

  const updateValueFromPointer = useCallback((
    pointerEvent: ReactPointerEvent<SVGCircleElement>,
    activeDragState: DragState,
  ) => {
    const svg = pointerEvent.currentTarget.ownerSVGElement
    const screenMatrix = svg?.getScreenCTM()
    if (!svg || !screenMatrix) return
    const svgPoint = svg.createSVGPoint()
    svgPoint.x = pointerEvent.clientX
    svgPoint.y = pointerEvent.clientY
    const localPoint = svgPoint.matrixTransform(screenMatrix.inverse())
    const rawValue = valueForY(localPoint.y)
    setLossOfControlValue(
      activeDragState.plan,
      activeDragState.bound,
      activeDragState.dateString,
      roundForStorage(rawValue),
    )
  }, [valueForY, setLossOfControlValue])

  const dragStateRef = useRef<DragState | null>(null)
  dragStateRef.current = dragState

  const handlePointerDown = useCallback((
    pointerEvent: ReactPointerEvent<SVGCircleElement>,
    plan: PlanId,
    bound: LossOfControlBound,
    dateString: string,
  ) => {
    pointerEvent.preventDefault()
    pointerEvent.stopPropagation()
    pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId)
    const nextDragState: DragState = {
      pointerId: pointerEvent.pointerId,
      plan,
      bound,
      dateString,
    }
    setDragState(nextDragState)
    updateValueFromPointer(pointerEvent, nextDragState)
  }, [updateValueFromPointer])

  const handlePointerMove = useCallback((
    pointerEvent: ReactPointerEvent<SVGCircleElement>,
  ) => {
    const activeDragState = dragStateRef.current
    if (!activeDragState) return
    if (activeDragState.pointerId !== pointerEvent.pointerId) return
    pointerEvent.preventDefault()
    updateValueFromPointer(pointerEvent, activeDragState)
  }, [updateValueFromPointer])

  const handlePointerUp = useCallback((
    pointerEvent: ReactPointerEvent<SVGCircleElement>,
  ) => {
    const activeDragState = dragStateRef.current
    if (!activeDragState) return
    if (activeDragState.pointerId !== pointerEvent.pointerId) return
    pointerEvent.currentTarget.releasePointerCapture(pointerEvent.pointerId)
    setDragState(null)
  }, [])

  const renderDotsForBound = (
    bound: LossOfControlBound,
    boundData: GraphDatum[],
  ) => {
    return boundData.map(datum => {
      const timestamp = dateStringToTimestamp(datum.date)
      const cx = xForTimestamp(timestamp)
      const cy = yForValue(datum.value)
      const isDragging =
        dragState?.bound === bound
        && dragState.dateString === datum.date
        && dragState.plan === activePlan
      const onPointerDown = (event: ReactPointerEvent<SVGCircleElement>) =>
        handlePointerDown(event, activePlan, bound, datum.date)
      return (
        <g key={`${bound}-${datum.date}`}>
          <EditorDot
            cx={cx}
            cy={cy}
            bound={bound}
            isDragging={isDragging}
            onPointerDown={onPointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />
          {isDragging ? (
            <text
              x={cx + DOT_HIT_RADIUS + 2}
              y={cy}
              fontSize={9}
              fill="#000"
              dominantBaseline="middle"
              pointerEvents="none"
              style={{ textShadow: '0 0 2px #fff, 0 0 2px #fff' }}
            >
              {datum.value.toFixed(2)}
            </text>
          ) : null}
        </g>
      )
    })
  }

  return (
    <g>
      {renderDotsForBound('max', visibleMaxData)}
      {renderDotsForBound('min', visibleMinData)}
    </g>
  )
}
